import { useMemo, useState } from 'react'
import useSWR from 'swr'
import { useDebounce } from '../hooks'
import { useToast } from '../contexts/ToastContext'
import {
  fetchPromotions,
  deletePromotion,
  type Promotion,
  type PromotionStatus,
} from '../services/promotionsApi'
import { Badge, Button } from '../components'
import type { BadgeVariant } from '../components'
import PromotionForm from './PromotionForm'
import pageStyles from '../styles/page.module.scss'
import listTableStyles from '../styles/list-table.module.scss'
import styles from './PromotionsList.module.scss'

const STATUS_VARIANT: Record<PromotionStatus, BadgeVariant> = {
  DRAFT: 'neutral',
  SCHEDULED: 'secondary',
  LIVE: 'live',
  EXPIRED: 'warning',
}

const formatWindow = (promo: Promotion): string => {
  const fmt = (iso: string | null) =>
    iso ? new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }) : null
  const start = fmt(promo.starts_at)
  const end = fmt(promo.ends_at)
  if (!start && !end) return 'Always'
  return `${start ?? '—'} → ${end ?? '—'}`
}

// Two promotions' [starts_at, ends_at) windows overlap (null = open-ended).
const windowsOverlap = (a: Promotion, b: Promotion): boolean => {
  const ms = (iso: string | null, fallback: number) => (iso ? new Date(iso).getTime() : fallback)
  const aStart = ms(a.starts_at, -Infinity)
  const aEnd = ms(a.ends_at, Infinity)
  const bStart = ms(b.starts_at, -Infinity)
  const bEnd = ms(b.ends_at, Infinity)
  return aStart < bEnd && bStart < aEnd
}

export default function PromotionsList() {
  const { data, isLoading, error, mutate } = useSWR('promotions', fetchPromotions, {
    revalidateOnFocus: false,
  })
  const { toast } = useToast()

  const [searchTerm, setSearchTerm] = useState('')
  const debouncedSearch = useDebounce(searchTerm, 300)

  // null = closed; '' = create (new); id = edit that promotion.
  const [editing, setEditing] = useState<string | null | undefined>(undefined)
  const [deletingId, setDeletingId] = useState<string | null>(null)

  const promotions = useMemo(() => data?.promotions ?? [], [data])

  // Any promotion that overlaps another ENABLED promotion on a shared offer. The
  // backend now blocks new overlaps, so this only surfaces ones that predate the
  // guard or were created out of band. Computed from the list — no extra call.
  const conflictingIds = useMemo(() => {
    const enabled = promotions.filter((p) => p.enabled)
    const ids = new Set<string>()
    for (let i = 0; i < enabled.length; i += 1) {
      for (let j = i + 1; j < enabled.length; j += 1) {
        const a = enabled[i]
        const b = enabled[j]
        const sharesOffer = a.offer_codes.some((code) => b.offer_codes.includes(code))
        if (sharesOffer && windowsOverlap(a, b)) {
          ids.add(a.tc_promotion_id)
          ids.add(b.tc_promotion_id)
        }
      }
    }
    return ids
  }, [promotions])

  const filtered = useMemo(() => {
    const query = debouncedSearch.trim().toLowerCase()
    if (!query) return promotions
    return promotions.filter((p) =>
      `${p.tc_name ?? ''} ${p.code} ${p.coupon_code}`.toLowerCase().includes(query)
    )
  }, [promotions, debouncedSearch])

  const handleDelete = async (promo: Promotion) => {
    if (!window.confirm(`Delete promotion "${promo.tc_name || promo.code}"? This cannot be undone.`)) return
    setDeletingId(promo.tc_promotion_id)
    try {
      await deletePromotion(promo.tc_promotion_id)
      await mutate()
      toast('Promotion deleted', 'success')
    } catch (err) {
      toast(err instanceof Error ? err.message : 'Failed to delete promotion', 'error')
    } finally {
      setDeletingId(null)
    }
  }

  if (error) {
    return (
      <div className={pageStyles.pageContainer}>
        <div className={pageStyles.error}>
          <h2>Couldn’t load promotions</h2>
          <p>{(error as Error).message}</p>
          <Button variant="secondary" onClick={() => void mutate()}>Retry</Button>
        </div>
      </div>
    )
  }

  return (
    <div className={pageStyles.pageContainer}>
      <div className={pageStyles.headerButtons}>
        <Button onClick={() => setEditing('')}>New promotion</Button>
      </div>

      <div className={pageStyles.pageHeader}>
        <h1>Promotions</h1>
        <p>
          Turn a Cleeng coupon into a merchandised promotion: assign it to eligible offers, set a
          display window, and enable it. Coupons themselves are created in the Cleeng console.
        </p>
      </div>

      <div className={listTableStyles.searchSection}>
        <div className={listTableStyles.searchForm}>
          <div className={listTableStyles.searchInputGroup}>
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search by name, code, or coupon…"
              className={listTableStyles.searchInput}
            />
            {searchTerm && (
              <button type="button" onClick={() => setSearchTerm('')} className={listTableStyles.clearButton}>
                Clear
              </button>
            )}
          </div>
        </div>
      </div>

      {isLoading && !data ? (
        <div className={pageStyles.loading}>
          <h3>Loading…</h3>
        </div>
      ) : filtered.length === 0 ? (
        <div className={listTableStyles.noResults}>
          <h3>No promotions {debouncedSearch ? 'match your search' : 'yet'}</h3>
          {!debouncedSearch && <p>Create one from a Cleeng coupon to get started.</p>}
        </div>
      ) : (
        <>
          <div className={`${listTableStyles.tableHeader} ${styles.listHeader}`}>
            <div>Promotion</div>
            <div>Coupon</div>
            <div>Window</div>
            <div>Offers</div>
            <div>Status</div>
            <div></div>
          </div>
          <div className={listTableStyles.tableBody}>
            {filtered.map((promo) => {
              const hasConflict = conflictingIds.has(promo.tc_promotion_id)
              return (
              <div key={promo.tc_promotion_id} className={`${listTableStyles.tableRow} ${styles.listItem}`}>
                <div className={listTableStyles.cellPrimary}>
                  <p className={listTableStyles.primaryText}>{promo.tc_name || promo.code}</p>
                  <p className={listTableStyles.secondaryText}>{promo.code}</p>
                </div>
                <div className={listTableStyles.cellId}>{promo.coupon_code}</div>
                <div className={listTableStyles.cellText}>{formatWindow(promo)}</div>
                <div className={listTableStyles.cellText}>{promo.offer_codes.length}</div>
                <div className={listTableStyles.cellBadges}>
                  <Badge variant={STATUS_VARIANT[promo.status]}>{promo.status}</Badge>
                  {hasConflict && (
                    <span title="Overlaps another enabled promotion on a shared offer">
                      <Badge variant="warning">Conflict</Badge>
                    </span>
                  )}
                </div>
                <div className={`${listTableStyles.cellActions} ${styles.actionsCell}`}>
                  <Button variant="secondary" size="small" onClick={() => setEditing(promo.tc_promotion_id)}>
                    Edit
                  </Button>
                  <Button
                    variant="danger"
                    size="small"
                    disabled={deletingId === promo.tc_promotion_id}
                    onClick={() => void handleDelete(promo)}
                  >
                    Delete
                  </Button>
                </div>
              </div>
              )
            })}
          </div>
        </>
      )}

      {editing !== undefined && (
        <PromotionForm
          isOpen
          promotionId={editing || null}
          onClose={() => setEditing(undefined)}
          onSaved={() => {
            setEditing(undefined)
            void mutate()
          }}
        />
      )}
    </div>
  )
}
